import {controller, attr} from '@github/catalyst'
import {waitForConsentPreferences} from '@github-ui/cookie-consent'
import {doNotTrack} from '@github-ui/do-not-track'
import {isFeatureEnabled} from '@github-ui/feature-flags'

import {init} from '@fullstory/browser'

const fsOrgID = 'o-1FH3DA-na1'

/**
 * Buckets a user into a deterministic variant using a simple hash function.
 * Returns a number between 0 and n-1.
 *
 * @param id - The user ID to bucket.
 * @param salt - A string to salt the hash.
 * @param n - The number of variants. (e.g. 2 for a 50% split, 3 for a 33% split, etc.)
 * @returns number The variant number (0 to n-1)
 */
const variantBucket = (id: string, salt: string, n: number): number => {
  const input = `${id}:${salt}`

  // Build a hash
  let hash = 0
  for (let i = 0; i < input.length; i++) {
    const char = input.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash
  }

  // Take absolute value to ensure positive number, then modulo to get bucket 0 to n-1
  return Math.abs(hash) % n
}

@controller
export class FullstoryCaptureElement extends HTMLElement {
  @attr fsScriptDomain = ''

  async connectedCallback() {
    if (doNotTrack()) {
      return
    }

    // Check if FullStory sampling is enabled via cookie
    if (!this.isUserSampled()) {
      return
    }

    const consent = await waitForConsentPreferences()
    if (consent?.['Analytics']) {
      const fsScriptUrl = new URL('./fs.js', import.meta.url).toString().split('//')[1]
      init({
        orgId: fsOrgID,
        script: fsScriptUrl,
      })
    }
  }

  isUserSampled(): boolean {
    if (!isFeatureEnabled('marketing_fullstory_sampling')) {
      return true
    }

    // Use a regular expression to extract the _octo cookie value
    const match = document.cookie.match(/(?:^|;\s*)_octo=([^;]*)/)
    const octoCookie = match?.[1]

    if (!octoCookie) {
      // If the octo cookie is not set, don't sample.
      return false
    }

    const bucket = variantBucket(octoCookie, 'fs_sampling', 2)

    return bucket === 1
  }
}
